import React from "react";
import { Link } from "react-router-dom";

/**
 * Homepage — Integrations section.
 * Radial hub diagram: source systems (left) → LaneRunner API (center) → outputs (right).
 * Desktop uses a fixed-aspect canvas with percentage-positioned circular icon badges and
 * an inline SVG connector layer so proportions hold at every width.
 * Tablet/mobile stack the flow vertically.
 */

const s = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

const IMonitor: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} {...s}>
    <rect x="3" y="4.5" width="18" height="12" rx="1.6" />
    <path d="M9 20h6M12 16.5V20" />
  </svg>
);
const IDatabase: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} {...s}>
    <ellipse cx="12" cy="6" rx="7.5" ry="3" />
    <path d="M4.5 6v6c0 1.7 3.4 3 7.5 3s7.5-1.3 7.5-3V6" />
    <path d="M4.5 12v6c0 1.7 3.4 3 7.5 3s7.5-1.3 7.5-3v-6" />
  </svg>
);
const IWrench: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} {...s}>
    <path d="M14.5 3.5a4.5 4.5 0 0 0 5.6 5.9L21 8.4 15.6 3l-1.1.5z" />
    <path d="M13.2 9.6L3.8 19a2 2 0 0 0 2.8 2.8l9.4-9.4" />
    <path d="M4.2 4.2l3.4 3.4M6.6 3l3.2 3.2" opacity="0.9" />
  </svg>
);
const IDoc: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} {...s}>
    <path d="M6.5 3.5h7L18 8v12.5h-11z" />
    <path d="M13.5 3.5V8H18M9.5 12h6M9.5 15.5h4" />
  </svg>
);
const ITag: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} {...s}>
    <path d="M12.5 3.5H20V11l-8.5 8.5-8-8z" />
    <circle cx="16.4" cy="7.6" r="1.3" />
  </svg>
);
const IBox: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} {...s}>
    <path d="M12 2.8l8.5 4.3v9.8L12 21.2 3.5 16.9V7.1z" />
    <path d="M3.5 7.1L12 11.4l8.5-4.3M12 11.4v9.8" />
  </svg>
);
const ICloudCode: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} {...s}>
    <path d="M6.8 18.5h10.4a3.6 3.6 0 0 0 .4-7.2 5.4 5.4 0 0 0-10.4-1.5 3.9 3.9 0 0 0-.4 8.7z" />
    <path d="M10.6 11.8L9.2 14l1.4 2.2M13.4 11.8L14.8 14l-1.4 2.2" />
  </svg>
);

const sources = [
  { icon: <IMonitor className="h-8 w-8" />, label: "TMS", cx: 262, cy: 80 },
  { icon: <IDatabase className="h-8 w-8" />, label: "ERP", cx: 233, cy: 225 },
  { icon: <IWrench className="h-8 w-8" />, label: "Internal Tools", cx: 238, cy: 370 },
];
const outputs = [
  { icon: <IDoc className="h-8 w-8" />, label: "Loads", cx: 1044, cy: 80 },
  { icon: <ITag className="h-8 w-8" />, label: "Quotes", cx: 1063, cy: 225 },
  { icon: <IBox className="h-8 w-8" />, label: "Shipment Status", cx: 1038, cy: 370 },
];

const W = 1380;
const H = 468;
const R = 45; // badge radius

const badgeCls =
  "absolute flex items-center justify-center rounded-full border border-[#EAEFEF] bg-white text-[#02857E] shadow-[0_6px_18px_rgba(14,27,26,0.07)]";

const Badge: React.FC<{
  icon: React.ReactNode;
  label: string;
  cx: number;
  cy: number;
}> = ({ icon, label, cx, cy }) => (
  <>
    <div
      className={badgeCls}
      style={{
        left: `${((cx - R) / W) * 100}%`,
        top: `${((cy - R) / H) * 100}%`,
        width: `${((R * 2) / W) * 100}%`,
        height: `${((R * 2) / H) * 100}%`,
      }}
    >
      {icon}
    </div>
    <div
      className="absolute flex items-center whitespace-nowrap text-[19px] leading-none text-[#25383A]"
      style={{
        left: `${((cx + R + 22) / W) * 100}%`,
        top: `${((cy - 12) / H) * 100}%`,
      }}
    >
      {label}
    </div>
  </>
);

/* ── connector layer (desktop) ─────────────────────────────────── */

const Connectors: React.FC = () => (
  <svg
    viewBox={`0 0 ${W} ${H}`}
    className="pointer-events-none absolute inset-0 h-full w-full"
    fill="none"
    aria-hidden
  >
    {/* faint dashed orbit */}
    <circle
      cx="686"
      cy="226"
      r="170"
      stroke="#02857E"
      strokeOpacity="0.16"
      strokeWidth="1"
      strokeDasharray="3 7"
    />
    <g stroke="#02857E" strokeWidth="1.5">
      <path d="M556 80C650 82 655 150 700 168" />
      <path d="M515 226H700" />
      <path d="M595 370C660 368 655 300 700 285" />
      <path d="M672 168C740 130 900 82 985 80" />
      <path d="M672 226H1000" />
      <path d="M672 285C740 322 900 368 985 370" />
    </g>
    <g fill="#02857E">
      <circle cx="556" cy="80" r="5" />
      <circle cx="515" cy="226" r="5" />
      <circle cx="595" cy="370" r="5" />
      <circle cx="985" cy="80" r="5" />
      <circle cx="1000" cy="226" r="5" />
      <circle cx="985" cy="370" r="5" />
      <circle cx="700" cy="168" r="5" fillOpacity="0.55" />
      <circle cx="700" cy="285" r="5" fillOpacity="0.55" />
    </g>
  </svg>
);

const HomeIntegrations: React.FC = () => (
  <section className="bg-[#F5F6F5] py-20 sm:py-24">
    <div className="mx-auto w-full max-w-[1540px] px-5 sm:px-8">
      <div className="text-[13px] font-semibold uppercase tracking-[0.22em] text-[#02857E]">
        Integrations
      </div>
      <h2 className="mt-5 font-serif text-[42px] font-bold leading-[1.05] tracking-[-0.02em] text-[#0E1B1A] sm:text-[58px] lg:text-[64px]">
        Built to plug into your stack.
      </h2>
      <p className="mt-6 max-w-[46ch] text-[19px] leading-[1.6] text-[#5A6B6D] sm:text-[21px]">
        Post loads, retrieve quotes, and sync shipment status programmatically — LaneRunner
        connects to the systems you already run.
      </p>

      {/* diagram */}
      <div className="mt-10 rounded-2xl border border-[#E4EAEC] bg-[#FAFBFB] p-6 sm:mt-12 sm:p-8">
        {/* desktop: fixed-aspect canvas */}
        <div className="relative hidden aspect-[1380/468] w-full lg:block">
          <Connectors />

          {/* center hub */}
          <div
            className="absolute rounded-full"
            style={{
              left: `${((686 - 150) / W) * 100}%`,
              top: `${((226 - 150) / H) * 100}%`,
              width: `${(300 / W) * 100}%`,
              height: `${(300 / H) * 100}%`,
              background:
                "radial-gradient(circle, rgba(2,133,126,0.10) 0%, rgba(2,133,126,0) 70%)",
            }}
          />
          <div
            className="absolute flex flex-col items-center justify-center rounded-full border border-[#02857E]/25 bg-gradient-to-b from-white to-[#F1F8F7] shadow-[0_10px_36px_rgba(2,133,126,0.12)]"
            style={{
              left: `${((686 - 122) / W) * 100}%`,
              top: `${((226 - 122) / H) * 100}%`,
              width: `${(244 / W) * 100}%`,
              height: `${(244 / H) * 100}%`,
            }}
          >
            <span className="text-[#02857E]">
              <ICloudCode className="h-9 w-9" />
            </span>
            <span className="mt-3 text-center text-[26px] font-semibold leading-[1.15] tracking-[-0.01em] text-[#0E1B1A]">
              LaneRunner
              <br />
              API
            </span>
          </div>

          {[...sources, ...outputs].map((n) => (
            <Badge key={n.label} {...n} />
          ))}
        </div>

        {/* tablet / mobile: stacked flow */}
        <div className="flex flex-col items-center gap-3 lg:hidden">
          {sources.map((n) => (
            <div key={n.label} className="flex w-full items-center gap-4">
              <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full border border-[#EAEFEF] bg-white text-[#02857E] shadow-[0_6px_18px_rgba(14,27,26,0.07)]">
                {n.icon}
              </span>
              <span className="text-[17px] text-[#25383A]">{n.label}</span>
            </div>
          ))}
          <div className="flex justify-center py-1 text-[#02857E]">
            <svg viewBox="0 0 24 40" className="h-8 w-6" {...s}>
              <path d="M12 2v32" />
              <path d="M7 29l5 5 5-5" />
            </svg>
          </div>
          <div className="flex h-40 w-40 flex-col items-center justify-center rounded-full border border-[#02857E]/25 bg-gradient-to-b from-white to-[#F1F8F7] shadow-[0_10px_36px_rgba(2,133,126,0.12)]">
            <span className="text-[#02857E]">
              <ICloudCode className="h-8 w-8" />
            </span>
            <span className="mt-2 text-center text-[19px] font-semibold leading-tight text-[#0E1B1A]">
              LaneRunner
              <br />
              API
            </span>
          </div>
          <div className="flex justify-center py-1 text-[#02857E]">
            <svg viewBox="0 0 24 40" className="h-8 w-6" {...s}>
              <path d="M12 2v32" />
              <path d="M7 29l5 5 5-5" />
            </svg>
          </div>
          {outputs.map((n) => (
            <div key={n.label} className="flex w-full items-center gap-4">
              <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full border border-[#EAEFEF] bg-white text-[#02857E] shadow-[0_6px_18px_rgba(14,27,26,0.07)]">
                {n.icon}
              </span>
              <span className="text-[17px] text-[#25383A]">{n.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* CTAs */}
      <div className="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:gap-7">
        <Link
          to="/shippers"
          className="inline-flex items-center gap-2 text-[17px] font-semibold text-[#02857E] hover:underline"
        >
          Explore Integrations
          <span aria-hidden>→</span>
        </Link>
        <span aria-hidden className="hidden h-6 w-px bg-[#DCE6E7] sm:block" />
        <a
          href="mailto:support@lanerunner.com?subject=API%20access"
          className="inline-flex items-center gap-2 text-[16px] text-[#5A6B6D] hover:text-[#0E1B1A]"
        >
          Talk to us about API access
          <span aria-hidden>→</span>
        </a>
      </div>
    </div>
  </section>
);

export default HomeIntegrations;
